import { useCallback, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import InlineError from "../components/inline-error.js";
import { FillContainerLoader } from "../components/loader.js";
import trpc from "../utils/trpc.js";
import { sanitizeCanvasHtml } from "../utils/canvas.js";
import { Button } from "../components/button/button.js";
import ProgressBar from "../components/progress-bar.js";
import { QuizQuestionCard } from "../components/quiz-question-card.js";

export function Component() {
  const navigate = useNavigate();

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: assessmentInfo, isLoading: isInfoLoading } = trpc.course.getAssessmentInfo.useQuery(undefined, {
    staleTime: Infinity,
  });

  const {
    data: quizData,
    isLoading: isQuizLoading,
    error,
  } = trpc.course.getAssessmentQuiz.useQuery(undefined, {
    staleTime: Infinity,
    enabled: assessmentInfo?.required === true && assessmentInfo.completed === false,
  });

  const submitMutation = trpc.course.submitAssessment.useMutation();
  const utils = trpc.useUtils();

  const questionEntryIds = useMemo(() => {
    if (!quizData) return [];
    return quizData.items
      .map(item => {
        const entry = item.entry;
        return entry ? String(entry["id"]) : null;
      })
      .filter((id): id is string => id !== null);
  }, [quizData]);

  const allAnswered = questionEntryIds.length > 0 && questionEntryIds.every(id => answers[id] !== undefined);

  const handleSelect = useCallback((questionEntryId: string, choiceId: string) => {
    setAnswers(prev => ({ ...prev, [questionEntryId]: choiceId }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!allAnswered || !quizData) return;
    setIsSubmitting(true);
    try {
      const result = await submitMutation.mutateAsync({ assessmentId: quizData.assessmentId, answers });
      if (result.allCompleted) {
        // Optimistically update cache so AssessmentGate passes cleanly before navigation
        utils.course.getAssessmentInfo.setData(undefined, prev => (prev ? { ...prev, completed: true } : undefined));
        // Refetch getModules so learning journey reflects any newly unlocked modules after assessment completion
        await utils.course.getModules.invalidate();
        navigate("/", { replace: true });
        return;
      }
      await utils.course.getAssessmentQuiz.invalidate();
      await utils.course.getAssessmentInfo.invalidate();
      setAnswers({});
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error(e);
    }
    setIsSubmitting(false);
  }, [allAnswered, answers, quizData, submitMutation, utils, navigate]);

  const answeredCount = questionEntryIds.filter(id => answers[id] !== undefined).length;
  const total = questionEntryIds.length;
  const progressPercent = total > 0 ? Math.round((answeredCount / total) * 100) : 0;

  if (isInfoLoading || isQuizLoading || isSubmitting)
    return (
      <div className="h-screen">
        <FillContainerLoader delayMs={0} size="large" />
      </div>
    );

  // If no assessment is required or already completed, redirect to home
  if (assessmentInfo && (!assessmentInfo.required || assessmentInfo.completed)) {
    return <Navigate to="/" replace />;
  }

  if (error) return <InlineError message="We couldn't load the assessment. Please try refreshing." />;
  if (!quizData) return <div className="text-sm">No assessment available.</div>;

  const { quiz, items } = quizData;

  return (
    <div className="min-h-screen">
      <div className="mx-auto flex max-w-screen-xl flex-col px-6 lg:flex-row lg:px-8">
        {/* Sidebar */}
        <aside className="w-full py-8 lg:sticky lg:top-0 lg:w-[26rem] lg:shrink-0 lg:self-start lg:py-12 lg:pr-8">
          <div className="flex flex-col gap-8 rounded-xl border border-gray bg-white/60 p-6 shadow-lg shadow-lac-leman/10 backdrop-blur-xl lg:max-h-[calc(100vh-6rem)] lg:p-8">
            <div className="min-w-0 border-b border-gray pb-6">
              {assessmentInfo && assessmentInfo.total > 1 && (
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-lac-leman">
                  Assessment {assessmentInfo.completedCount + 1} of {assessmentInfo.total}
                </p>
              )}
              <h1 className="text-2xl font-semibold leading-snug">{quiz.title}</h1>
            </div>

            {quiz.instructions && (
              <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-midnight/40">Instructions</p>
                <div
                  className="_isolated-root text-sm leading-relaxed text-midnight/70"
                  dangerouslySetInnerHTML={{ __html: sanitizeCanvasHtml(quiz.instructions) }}
                />
              </div>
            )}

            <div className="hidden flex-col gap-3 lg:flex">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-midnight/60">Answered</span>
                <span className="text-base font-semibold text-lac-leman">
                  {answeredCount} / {total}
                </span>
              </div>
              <ProgressBar progress={progressPercent} />
            </div>

            <Button
              filled="lac-leman"
              disabled={!allAnswered || isSubmitting}
              loading={isSubmitting}
              onClick={handleSubmit}
              className="hidden w-full py-2.5 text-sm lg:block">
              Submit
            </Button>
          </div>
        </aside>

        {/* Question list */}
        <main className="flex-1 py-8 pb-28 lg:py-12 lg:pb-12 lg:pl-10">
          <div className="flex flex-col gap-8">
            {items.map((item, index) => {
              const entry = item.entry;
              if (!entry) return null;
              const entryId = String(entry["id"]);
              const itemBody = entry["item_body"];
              const interactionData = entry["interaction_data"] as
                | { choices?: Array<{ id: string; item_body: string }> }
                | undefined;
              const choices = interactionData?.choices ?? [];

              return (
                <QuizQuestionCard
                  key={entryId}
                  index={index}
                  entryId={entryId}
                  itemBody={itemBody}
                  choices={choices}
                  selectedChoiceId={answers[entryId]}
                  onSelect={choiceId => handleSelect(entryId, choiceId)}
                  variant="grid"
                />
              );
            })}
          </div>
        </main>
      </div>

      {/* Mobile bottom dock */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray bg-white/80 px-6 py-4 backdrop-blur-md lg:hidden">
        <div className="mx-auto flex max-w-screen-xl items-center gap-6">
          <div className="flex flex-1 flex-col gap-1.5">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-midnight/60">Answered</span>
              <span className="text-sm font-semibold text-lac-leman">
                {answeredCount} / {total}
              </span>
            </div>
            <ProgressBar progress={progressPercent} />
          </div>
          <Button
            filled="lac-leman"
            disabled={!allAnswered || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit}
            className="shrink-0 px-6 py-2.5 text-sm">
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
}
